import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SensorDetail, DEVICE_TYPE_KEYS } from '../../core/services/equipement.service';

@Component({
  selector: 'app-capteur-detail-modal',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './capteur-detail-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./capteur-detail-modal.component.scss'],
})
export class CapteurDetailModalComponent implements OnInit, OnDestroy {
  @Input() detail!: SensorDetail;
  @Input() loading = false;
  @Output() close = new EventEmitter<void>();

  private readonly translate = inject(TranslateService);

  /** Traduit deviceTypeCode côté client — deviceType (libellé) est renvoyé pré-formaté en français par le backend. */
  deviceTypeLabel(code: string | null): string {
    const key = code ? DEVICE_TYPE_KEYS[code] : undefined;
    return key ? this.translate.instant('equipements.upsertModal.deviceTypes.' + key) : (code ?? '');
  }

  ngOnInit() {
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;
  }

  ngOnDestroy() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  onBackdrop() { this.close.emit(); }

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }

  formatShortDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }

  formatRssi(v: number | null): string { return v !== null ? `${v} dBm` : '—'; }
  formatSnr(v: number | null): string  { return v !== null ? `${v} dB`  : '—'; }
  formatSf(v: number | null): string   { return v !== null ? `SF${v}`   : '—'; }
  formatInt(v: number | null): string  { return v !== null ? `${v}`     : '—'; }

  getRssiClass(v: number | null): string {
    if (v === null) return 'val-unknown';
    if (v >= -85) return 'val-ok';
    if (v >= -100) return 'val-warn';
    return 'val-danger';
  }

  formatPayload(p: Record<string, unknown> | null): string {
    if (!p) return '—';
    return JSON.stringify(p, null, 2);
  }

  payloadEntries(p: Record<string, unknown> | null): { key: string; value: string; hint?: string }[] {
    if (!p) return [];
    return Object.entries(p).map(([key, value]) => {
      const strVal = String(value);
      let hint: string | undefined;
      if ((key === 'master_battery' || key === 'node_battery' || key === 'battery') && value === 255) {
        hint = 'alimentation externe';
      }
      return { key, value: strVal, hint };
    });
  }

  truncateCursor(cursor: string | null): string {
    if (!cursor) return '—';
    return cursor.length > 24 ? cursor.slice(0, 24) + '…' : cursor;
  }
}
